import { useCallback } from 'react';

import type { LogErrorFn, SetContextFn } from '@strava/container-context';
import { useStravaErrorLogging } from '@strava/container-context';

import { getDeviceMemoryInfo } from '../helpers/getDeviceMemoryInfo';
import { useMRE } from '../hooks/useMRE';
import { useMREState } from '../hooks/useMREState';

export const useMRELogError = () => {
  const { logError: _logError, withScope } = useStravaErrorLogging();

  const { page, versionInfo } = useMRE();
  const { mapType, isFlyoverInProgress, pageIsUnloadingRef } = useMREState();

  return useCallback(
    (
      error: Parameters<LogErrorFn>[0],
      context?: {
        name: Parameters<SetContextFn>[0];
        context: Parameters<SetContextFn>[1];
      },
      tags?: Record<string, string | number | boolean>
    ) => {
      withScope((scope) => {
        // Sentry tags
        scope.setTags({
          ...tags,
          'mre.page': page,
          'mre.mapType': mapType?.toString(),
          'mre.isFlyoverInProgress': isFlyoverInProgress.toString(),
          'mre.version': versionInfo?.versionString,
          'mre.pageIsUnloading': pageIsUnloadingRef.current
        });
        if (context) {
          scope.setContext(context.name, context.context);
        }

        // extra diagnostic context
        scope.setContext('Memory', getDeviceMemoryInfo());
        scope.setContext('Miscellaneous', {
          visibilityState: document.visibilityState,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          effectiveNetworkType: (navigator as any).connection?.effectiveType
        });

        if (
          typeof error === 'string' &&
          error.toLowerCase().includes('memory')
        ) {
          scope.setTag('mre.isMemoryError', true);
        }

        _logError(error);
      });
    },
    [
      _logError,
      withScope,
      page,
      isFlyoverInProgress,
      mapType,
      versionInfo,
      pageIsUnloadingRef
    ]
  );
};
